import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Autocomplete,
  Divider,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useMemo } from "react";
import {
  CopilotAutofillElement,
  FormDefinitionType,
  FormElementType,
} from "../../../types";
import { ButtonGroupFunction } from "../../task-types/form/wizard-form-elements/TaskButtonGroupFormComponent";
import { FormElementChangeType } from "../FormBuilder";
import { useFormBuilderContext } from "../FormBuilderProvider";
import {
  ButtonGroupFunctionLabels,
  StepOption,
} from "../wizard/button-group/ButtonGroupSideSectionDisplay";

const CopilotAutofillSideSectionDisplay = ({
  element,
  onChange,
}: {
  element: CopilotAutofillElement;
  onChange: (props: FormElementChangeType) => void;
}) => {
  const { elements, task } = useFormBuilderContext();
  const isWizard = task.data?.isWizard;

  const options = useMemo(() => {
    const arr: Array<StepOption> = [];
    elements?.forEach((e) => {
      if (e.type === FormElementType.Heading) {
        arr.push({
          name: e.label,
          index: arr.length,
        });
      }
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  if (isWizard)
    return (
      <>
        <Divider />
        <TextField
          label={
            <span>
              Copilot Bias
              <Tooltip title="Bias is an optional prompt or instruction that influences Copilot's behavior for when extracting data from a document. Helpful for providing more context for Copilot to work with.">
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }} tabIndex={-1}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          }
          value={element.bias}
          multiline
          minRows={3}
          onChange={(event) => {
            onChange?.({
              changeType: "update",
              newElement: {
                ...element,
                bias: event.target.value || undefined,
              } as FormDefinitionType,
            });
          }}
        />
        <TextField
          label="Allowed File Types"
          value={element.fileTypes}
          onChange={(event) => {
            onChange?.({
              changeType: "update",
              newElement: {
                ...element,
                fileTypes: event.target.value || undefined,
              } as FormDefinitionType,
            });
          }}
        />
        <Divider />

        {task.data?.isWizard && (
          <Autocomplete
            options={Object.values(ButtonGroupFunction).map((value) => ({
              label: ButtonGroupFunctionLabels[value],
              value,
            }))}
            getOptionLabel={(option) => option.label}
            value={
              element.onExecute
                ? {
                    label: ButtonGroupFunctionLabels[element.onExecute],
                    value: element.onExecute,
                  }
                : undefined
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(event, newValue) => {
              onChange?.({
                changeType: "update",
                newElement: {
                  ...element,
                  onExecute: newValue ? newValue.value : undefined,
                } as FormDefinitionType,
              });
            }}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label="Action Upon Completion" />
            )}
          />
        )}
        {task.data?.isWizard &&
          element.onExecute == ButtonGroupFunction.GOTO_STEP && (
            <Autocomplete
              options={options}
              getOptionLabel={(option) => option.name}
              value={options.find((opt) => opt.index === element.data) || null}
              isOptionEqualToValue={(option, value) =>
                option.index === value.index
              }
              onChange={(event, newValue) => {
                onChange({
                  changeType: "update",
                  newElement: {
                    ...element,
                    data: newValue?.index,
                  } as FormDefinitionType,
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Go to Step" />
              )}
            />
          )}
      </>
    );
  else return <></>;
};

export default CopilotAutofillSideSectionDisplay;
